import { useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { emReais } from '../../encomendas';
import type { OfertaDaDistribuidora, ProdutoAoVivo, ProdutoDaConsulta } from '../../encomendas';
import { criarEncomenda } from '../../encomendas-fluxo';
import type { NovaEncomenda } from '../../encomendas-fluxo';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Campo, Gaveta, Opcoes } from './Gaveta';

/**
 * ENCOMENDAR (fase 3) — a gaveta que abre ao clicar "Encomendar" numa distribuidora. Não sai da
 * consulta. O cliente fica só nesta encomenda (decisão do dono: sem cadastro de cliente).
 * O custo/PMC/preço mostrados no resumo são CONGELADOS pelo servidor ao salvar — a tela não manda
 * preço nenhum.
 */

// Pagamento na encomenda: só PAGO ou A PAGAR (dono, 02/10 — sem troco, sem sinal na tela).
type Pagamento = 'NAO_PAGO' | 'PAGO';
type Receita = 'NAO_NECESSITA' | 'FICOU_NA_LOJA' | 'TRAZ_NA_RETIRADA' | 'ENVIOU_DIGITAL' | 'OUTRO';

const digitos = (t: string) => t.replace(/\D/g, '');
const paraDecimal = (t: string): string | null => {
  const v = t.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  return /^\d{1,7}(\.\d{1,2})?$/.test(v) ? v : null;
};
const doCentavo = (c: number) => (c / 100).toFixed(2).replace('.', ',');

function mascaraTelefone(t: string): string {
  const d = digitos(t).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function Encomendar({
  sessao,
  produto,
  aoVivo,
  oferta,
  aoFechar,
  aoCriar,
}: {
  sessao: Sessao;
  produto: ProdutoDaConsulta;
  aoVivo: ProdutoAoVivo | null;
  oferta: OfertaDaDistribuidora;
  aoFechar: () => void;
  aoCriar: (r: { id: string; codigo: string }) => void;
}) {
  const veCusto = useVeCusto();
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [modalidade, setModalidade] = useState<'RETIRADA' | 'ENTREGA'>('RETIRADA');
  const [end, setEnd] = useState({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    referencia: '',
  });
  const [pagamento, setPagamento] = useState<Pagamento>('NAO_PAGO');
  const precoInova = aoVivo?.precoVenda ?? oferta.precoInova;
  const [total, setTotal] = useState(
    precoInova ? doCentavo(Math.round(Number(precoInova) * 100)) : '',
  );
  const [totalEditado, setTotalEditado] = useState(false);
  const [receita, setReceita] = useState<Receita>(
    produto.exigeReceita ? 'TRAZ_NA_RETIRADA' : 'NAO_NECESSITA',
  );
  const [receitaObs, setReceitaObs] = useState('');
  const [observacao, setObservacao] = useState('');
  const [urgente, setUrgente] = useState(false);
  const [prazoTipo, setPrazoTipo] = useState<'SEM_PRAZO' | 'DATA' | 'DATA_HORA'>('SEM_PRAZO');
  const [prazoData, setPrazoData] = useState('');
  const [prazoHora, setPrazoHora] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tentou, setTentou] = useState(false);

  // total sugerido = preço Inova × quantidade (enquanto ninguém mexeu)
  const totalSugerido = precoInova
    ? doCentavo(Math.round(Number(precoInova) * 100) * quantidade)
    : '';
  const totalMostrado = totalEditado ? total : totalSugerido;
  const totalDec = paraDecimal(totalMostrado);
  const erros = useMemo(() => {
    const e: Record<string, string> = {};
    if (nome.trim().length < 2) e.nome = 'Informe o nome do cliente.';
    if (!/^\d{10,11}$/.test(digitos(telefone))) e.telefone = 'Telefone com DDD.';
    if (cpf && digitos(cpf).length !== 11) e.cpf = 'CPF com 11 dígitos (ou deixe em branco).';
    if (modalidade === 'ENTREGA') {
      if (end.logradouro.trim().length < 2) e.logradouro = 'Informe a rua.';
      if (!end.numero.trim()) e.numero = 'Informe o número.';
      if (end.bairro.trim().length < 2) e.bairro = 'Informe o bairro.';
      if (end.cep && digitos(end.cep).length !== 8) e.cep = 'CEP com 8 dígitos.';
    }
    if (pagamento === 'PAGO') {
      if (!totalDec || Number(totalDec) <= 0) e.total = 'Informe o valor que o cliente pagou.';
    } else if (totalMostrado && !totalDec) e.total = 'Valor inválido.';
    if (produto.exigeReceita && receita === 'NAO_NECESSITA')
      e.receita = 'Produto controlado: informe a situação da receita.';
    if (prazoTipo !== 'SEM_PRAZO' && !prazoData) e.prazo = 'Informe a data prometida.';
    if (prazoTipo === 'DATA_HORA' && !prazoHora) e.prazo = 'Informe a hora prometida.';
    return e;
  }, [
    nome,
    telefone,
    cpf,
    modalidade,
    end,
    pagamento,
    totalDec,
    totalMostrado,
    produto.exigeReceita,
    receita,
    prazoTipo,
    prazoData,
    prazoHora,
  ]);
  const mostrar = (k: string) => (tentou ? (erros[k] ?? null) : null);

  async function salvar() {
    setTentou(true);
    setErro(null);
    if (Object.keys(erros).length) return;
    const prazoEm =
      prazoTipo === 'SEM_PRAZO'
        ? null
        : new Date(
            `${prazoData}T${prazoTipo === 'DATA' ? '23:59' : prazoHora}:00-03:00`,
          ).toISOString();
    const corpo: NovaEncomenda = {
      cliente: { nome: nome.trim(), telefone: digitos(telefone), cpf: cpf ? digitos(cpf) : null },
      modalidade,
      endereco:
        modalidade === 'ENTREGA'
          ? {
              cep: end.cep ? digitos(end.cep) : null,
              logradouro: end.logradouro.trim(),
              numero: end.numero.trim(),
              complemento: end.complemento.trim() || null,
              bairro: end.bairro.trim(),
              referencia: end.referencia.trim() || null,
            }
          : null,
      itens: [
        {
          produtoId: produto.id,
          quantidade,
          fornecedorSlug: oferta.slug,
          fornecedorNome: oferta.distribuidora,
        },
      ],
      pagamento: {
        situacao: pagamento,
        valorTotalCombinado: totalDec,
        // Pago = o total combinado já recebido (a forma não é perguntada: dono, 02/10).
        recebido: pagamento === 'PAGO' && totalDec ? { valor: totalDec, forma: 'OUTRO' } : null,
      },
      receita: { situacao: receita, obs: receitaObs.trim() || null },
      observacao: observacao.trim() || null,
      urgente,
      prazo: { tipo: prazoTipo, em: prazoEm },
    };
    setEnviando(true);
    const r = await criarEncomenda(sessao, corpo);
    setEnviando(false);
    if (r.ok) aoCriar(r.dados);
    else
      setErro(
        r.falha.tipo === 'invalido' || r.falha.tipo === 'conflito'
          ? r.falha.mensagem || 'Confira os campos.'
          : r.falha.tipo === 'offline'
            ? 'Sem conexão. A encomenda NÃO foi salva — tente de novo.'
            : 'Não foi possível salvar. A encomenda NÃO foi salva.',
      );
  }

  return (
    <Gaveta
      titulo="Nova encomenda"
      subtitulo={`Via ${oferta.distribuidora} — o comprador pode comprar em outra distribuidora`}
      aoFechar={aoFechar}
      rodape={
        <>
          {erro && (
            <p className="enc-form-erro" role="alert">
              {erro}
            </p>
          )}
          <button type="button" className="enc-botao-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="enc-botao"
            onClick={() => void salvar()}
            disabled={enviando}
            aria-busy={enviando}
          >
            {enviando ? 'Salvando…' : 'Salvar encomenda'}
          </button>
        </>
      }
    >
      <section className="enc-resumo" aria-label="Resumo da consulta">
        <FotoDoProduto url={produto.foto} nome={produto.descricao} tamanho={56} />
        <div className="enc-resumo-id">
          <b>{produto.descricao}</b>
          <span>
            {produto.fabricante ?? '—'}
            {produto.ean ? ` · EAN ${produto.ean}` : ''}
          </span>
        </div>
        <dl className="enc-resumo-numeros">
          <div>
            <dt>Fornecedor consultado</dt>
            <dd>{oferta.distribuidora}</dd>
          </div>
          {veCusto && (
            <div>
              <dt>Custo consultado</dt>
              <dd className="enc-mono">{emReais(oferta.custo)}</dd>
            </div>
          )}
          <div>
            <dt>Preço de venda (HOS)</dt>
            <dd className="enc-mono">{emReais(precoInova)}</dd>
          </div>
          <div>
            <dt>PMC</dt>
            <dd className="enc-mono">{emReais(aoVivo?.pmc ?? oferta.pmc)}</dd>
          </div>
          <div>
            <dt>Estoque consultado</dt>
            <dd>
              {oferta.estoque !== null
                ? `${String(oferta.estoque)} un`
                : oferta.disponivel
                  ? 'Tem (sem quantidade)'
                  : '—'}
            </dd>
          </div>
        </dl>
        {produto.exigeReceita && (
          <p className="enc-alerta-receita">
            <Icone nome="warn" tamanho={15} traco={1.8} /> Produto controlado — exige receita.
          </p>
        )}
      </section>

      <fieldset className="enc-secao">
        <legend>Cliente</legend>
        <div className="enc-grade-form">
          <Campo rotulo="Nome" erro={mostrar('nome')} largo>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoComplete="off"
              maxLength={120}
            />
          </Campo>
          <Campo rotulo="Telefone (WhatsApp)" erro={mostrar('telefone')}>
            <input
              value={telefone}
              onChange={(e) => setTelefone(mascaraTelefone(e.target.value))}
              inputMode="tel"
              placeholder="(32) 99999-9999"
            />
          </Campo>
          <Campo rotulo="CPF (opcional)" erro={mostrar('cpf')}>
            <input
              value={cpf}
              onChange={(e) => setCpf(digitos(e.target.value).slice(0, 11))}
              inputMode="numeric"
            />
          </Campo>
        </div>
      </fieldset>

      <fieldset className="enc-secao">
        <legend>Quantidade e entrega</legend>
        <div className="enc-linha-form">
          <div className="enc-qtd" role="group" aria-label="Quantidade">
            <button
              type="button"
              onClick={() => setQuantidade((q) => Math.max(1, q - 1))}
              aria-label="Menos"
            >
              −
            </button>
            <input
              value={quantidade}
              onChange={(e) =>
                setQuantidade(Math.max(1, Math.min(9999, Number(digitos(e.target.value)) || 1)))
              }
              inputMode="numeric"
              aria-label="Quantidade"
            />
            <button
              type="button"
              onClick={() => setQuantidade((q) => Math.min(9999, q + 1))}
              aria-label="Mais"
            >
              +
            </button>
          </div>
          <Opcoes
            nome="Retirada ou entrega"
            valor={modalidade}
            aoMudar={setModalidade}
            opcoes={[
              ['RETIRADA', 'Retirada'],
              ['ENTREGA', 'Entrega'],
            ]}
          />
        </div>
        {modalidade === 'ENTREGA' && (
          <div className="enc-grade-form">
            <Campo rotulo="CEP" erro={mostrar('cep')}>
              <input
                value={end.cep}
                onChange={(e) => setEnd({ ...end, cep: digitos(e.target.value).slice(0, 8) })}
                inputMode="numeric"
              />
            </Campo>
            <Campo rotulo="Rua" erro={mostrar('logradouro')} largo>
              <input
                value={end.logradouro}
                onChange={(e) => setEnd({ ...end, logradouro: e.target.value })}
              />
            </Campo>
            <Campo rotulo="Número" erro={mostrar('numero')}>
              <input
                value={end.numero}
                onChange={(e) => setEnd({ ...end, numero: e.target.value })}
              />
            </Campo>
            <Campo rotulo="Complemento">
              <input
                value={end.complemento}
                onChange={(e) => setEnd({ ...end, complemento: e.target.value })}
              />
            </Campo>
            <Campo rotulo="Bairro" erro={mostrar('bairro')}>
              <input
                value={end.bairro}
                onChange={(e) => setEnd({ ...end, bairro: e.target.value })}
              />
            </Campo>
            <Campo rotulo="Referência" largo>
              <input
                value={end.referencia}
                onChange={(e) => setEnd({ ...end, referencia: e.target.value })}
              />
            </Campo>
          </div>
        )}
      </fieldset>

      <fieldset className="enc-secao">
        <legend>Pagamento</legend>
        <Opcoes
          nome="Situação do pagamento"
          valor={pagamento}
          aoMudar={setPagamento}
          opcoes={[
            ['NAO_PAGO', 'A pagar'],
            ['PAGO', 'Pago'],
          ]}
        />
        <div className="enc-grade-form">
          <Campo
            rotulo={pagamento === 'PAGO' ? 'Valor pago' : 'Valor combinado com o cliente'}
            dica={
              precoInova
                ? `Venda Inova ${emReais(precoInova)} × ${String(quantidade)}`
                : 'Fica congelado na encomenda'
            }
            erro={mostrar('total')}
          >
            <input
              value={totalMostrado}
              onChange={(e) => {
                setTotalEditado(true);
                setTotal(e.target.value);
              }}
              inputMode="decimal"
              placeholder="0,00"
            />
          </Campo>
        </div>
      </fieldset>

      <fieldset className="enc-secao">
        <legend>Receita</legend>
        <Opcoes
          nome="Receita"
          valor={receita}
          aoMudar={setReceita}
          opcoes={[
            ['NAO_NECESSITA', 'Não necessita'],
            ['FICOU_NA_LOJA', 'Ficou na loja'],
            ['TRAZ_NA_RETIRADA', 'Trará na retirada'],
            ['ENVIOU_DIGITAL', 'Enviou digital'],
            ['OUTRO', 'Outro'],
          ]}
        />
        {mostrar('receita') && <p className="enc-campo-msg">{mostrar('receita')}</p>}
        <Campo rotulo="Observação da receita" largo>
          <input
            value={receitaObs}
            onChange={(e) => setReceitaObs(e.target.value)}
            maxLength={300}
          />
        </Campo>
      </fieldset>

      <fieldset className="enc-secao">
        <legend>Prazo e observação</legend>
        <div className="enc-linha-form">
          <Opcoes
            nome="Prazo prometido ao cliente"
            valor={prazoTipo}
            aoMudar={setPrazoTipo}
            opcoes={[
              ['SEM_PRAZO', 'Sem prazo'],
              ['DATA', 'Data'],
              ['DATA_HORA', 'Data e hora'],
            ]}
          />
          <label className="enc-urgente">
            <input
              type="checkbox"
              checked={urgente}
              onChange={(e) => setUrgente(e.target.checked)}
            />{' '}
            Urgente
          </label>
        </div>
        {prazoTipo !== 'SEM_PRAZO' && (
          <div className="enc-grade-form">
            <Campo rotulo="Data prometida" erro={mostrar('prazo')}>
              <input type="date" value={prazoData} onChange={(e) => setPrazoData(e.target.value)} />
            </Campo>
            {prazoTipo === 'DATA_HORA' && (
              <Campo rotulo="Hora">
                <input
                  type="time"
                  value={prazoHora}
                  onChange={(e) => setPrazoHora(e.target.value)}
                />
              </Campo>
            )}
          </div>
        )}
        <Campo
          rotulo="Observação para a encomenda"
          dica="Ex.: aceita similar, precisa até sexta, recado para o comprador"
          largo
        >
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={3}
            maxLength={1000}
          />
        </Campo>
      </fieldset>
    </Gaveta>
  );
}
